import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Banner, ExtraButton, ActionButton, ShareButton } from '../types';
import { bannerService } from '../api/bannerService';
import { BannerPreview } from './BannerPreview';
import { getBannerDetails } from '../../../api/bannerManagement/getBannerDetails';
import { ExtraButtonsForm, areExtraButtonsValid } from './ExtraButtonsForm';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import {
  addBanner,
  updateBanner,
  uploadBannerImage,
} from '../../../api/bannerManagement';
import { transformExtraButtonsFromApi } from '../utils/extraButtonTransforms';
import Select from 'react-select';
import { getImageUrl } from '../../../utils/imageUrl';

interface BannerFormProps {
  show: boolean;
  onHide: () => void;
  banner?: Banner | null;
  onSuccess?: () => void;
}

const getInitialFormData = (): Omit<Banner, 'id'> => ({
  title: '',
  image: '',
  bannerLocation: 'home',
  type: 'custom',
  textHtml: '<p style="color:#FFFFFF;font-size:14px;font-weight:400;line-height:18px;margin:0;"></p>',
  sortNo: 1,
  hasMiscProperty: true,
  platform: 'app',
  extraButtons: [],
  isActive: true,
});

// Helper to parse query params from a path
function parseQueryParams(path: string): Record<string, string> & { pathname: string } {
  try {
    const url = new URL(path, 'http://dummy'); // dummy base for parsing
    const params = Object.fromEntries(url.searchParams.entries());
    return { pathname: url.pathname, ...params };
  } catch {
    return { pathname: '' };
  }
}

// Helper to generate S3 path for banner images
const generateBannerImagePath = (fileName: string) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `sfl-banners/${yyyy}-${mm}-${dd}/${fileName}`;
};

// Helper: Convert File/Blob to base64
function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper: Convert base64 to Blob
function base64ToBlob(base64: string, type: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type });
}

// Add these interfaces at the top of the file, after the imports
interface ButtonConfig {
  arrow: {
    left: boolean;
    right: boolean;
  };
  play: {
    left: boolean;
    right: boolean;
  };
}

interface RedirectionConfig {
  type: string;
  academyId?: string;
  academyName?: string;
  coachingCenterId?: string;
  tournamentId?: string;
  tournamentName?: string;
  sports?: string;
  tabName?: string;
  tabType?: string;
  weeklyReportId?: string;
  path?: string;
}

interface MiscConfig {
  button?: ButtonConfig;
  redirection?: RedirectionConfig;
}

// Helper for ctaIconType type guard
const validCtaIconTypes = [
  'play-icon-left',
  'play-icon-right',
  'arrow-icon-left',
  'arrow-icon-right',
  'none',
] as const;
type CtaIconType = typeof validCtaIconTypes[number];
function getValidCtaIconType(val: any): CtaIconType {
  return validCtaIconTypes.includes(val) ? val : 'play-icon-left';
}

// Helper for linkType type guard
const validLinkTypes = ['custom', 'inApp'] as const;
type LinkType = typeof validLinkTypes[number];
function getValidLinkType(val: any): LinkType {
  return validLinkTypes.includes(val) ? val : 'custom';
}

// Helper for inAppLink.type type guard
const validInAppLinkTypes = ['tournament', 'academy'] as const;
type InAppLinkType = typeof validInAppLinkTypes[number];
function getValidInAppLinkType(val: any): InAppLinkType {
  return validInAppLinkTypes.includes(val) ? val : 'tournament';
}

// Helper to map backend type to 'tournament' | 'academy'
function mapBackendTypeToInAppLinkType(val: any): 'tournament' | 'academy' {
  if (
    val === 'TournamentTab' ||
    val === 'TournamentScreen' ||
    val === 'tab' ||
    val === 'tournament'
  ) {
    return 'tournament';
  }
  return 'academy';
}

// Move this function to the top-level scope, outside of any block
function parseRedirection(misc: any) {
  if (!misc || !misc.redirection) return { type: '', fields: {} };
  const { type, path } = misc.redirection;
  if (type === 'tab' || type === 'Academy') {
    return { type: 'AcademyTab', fields: {} };
  }
  if (type === 'page' && path) {
    if (path.includes('/academyDetailScreen')) {
      const params = new URLSearchParams(path.split('?')[1]);
      return {
        type: 'AcademyDetails',
        fields: {
          academyId: params.get('academyId') || '',
          academyName: decodeURIComponent(params.get('title') || ''),
          coachingCenterId: params.get('centerId') || '',
        }
      };
    }
    if (path.includes('/tournamentMainScreen')) {
      const params = new URLSearchParams(path.split('?')[1]);
      if (params.get('tabName')) {
        // Tournament Tab
        return {
          type: 'TournamentTab',
          fields: {
            tournamentId: params.get('tournamentId') || '',
            tournamentName: decodeURIComponent(params.get('tournamentName') || ''),
            sports: params.get('sports') || '',
            tabName: params.get('tabName') || '',
            tabType: params.get('tabType') || '',
          }
        };
      } else {
        // Regular Tournament
        return {
          type: 'tournament',
          fields: {
            tournamentId: params.get('tournamentId') || '',
            tournamentName: decodeURIComponent(params.get('tournamentName') || ''),
            sports: params.get('sports') || '',
          }
        };
      }
    }
    if (path.includes('/viewWeeklyReport')) {
      const params = new URLSearchParams(path.split('?')[1]);
      return {
        type: 'weeklyReport',
        fields: {
          weeklyReportId: params.get('reportId') || '',
        }
      };
    }
  }
  return { type: '', fields: {} };
}

// Utility: Transform API extraButtons to flat form state
// DELETE the entire local definition of
// function transformExtraButtonsFromApi(extraButtons: any[]): ExtraButton[] { ... }

// Utility: Transform form state extraButtons to API format
function transformExtraButtonsToApi(extraButtons: ExtraButton[]): any[] {
  return (extraButtons || []).map((btn) => {
    if (btn.category === 'button') {
      let redirection: any = {};
      if (btn.redirectionType === 'AcademyDetailsScreen') {
        // For AcademyDetailsScreen, send all fields as top-level, do NOT include redirection object
        const { redirectionType, url, academyId, academyName, coachingCenterId, location, academyLocation, tournamentId, tournamentName, sports, tabName, tabType, weeklyReportId, tabIndex, ...rest } = btn;
        return {
          ...rest,
          redirectionType: 'AcademyDetailsScreen',
          academyId,
          academyName,
          coachingCenterId,
          location,
          academyLocation,
        };
      } else if (btn.redirectionType === 'Academy') {
        // For Academy, send redirectionType as a field, do NOT include redirection object
        const { redirectionType, url, academyId, academyName, coachingCenterId, location, tournamentId, tournamentName, sports, tabName, tabType, weeklyReportId, tabIndex, ...rest } = btn;
        return {
          ...rest,
          redirectionType: 'Academy',
        };
      } else if (btn.redirectionType === 'TournamentScreen') {
        // For TournamentScreen, send all fields as top-level, do NOT include redirection object
        // Include tournamentSeasonName only if seasonName exists (SEASON type, not DEFAULT)
        const { redirectionType, url, academyId, academyName, coachingCenterId, location, academyLocation, tournamentId, tournamentName, sports, tournamentSeason, seasonName, tabName, tabType, weeklyReportId, tabIndex, ...rest } = btn;
        const payload: any = {
          ...rest,
          redirectionType: 'TournamentScreen',
          tournamentId,
          tournamentName,
          sports,
        };
        if (tournamentSeason) {
          payload.tournamentSeasonId = tournamentSeason;
        }
        // Only include tournamentSeasonName if seasonName exists (not DEFAULT season)
        if (seasonName) {
          payload.tournamentSeasonName = seasonName;
        }
        return payload;
      } else if (btn.redirectionType === 'TournamentTab') {
        // For TournamentTab, send all fields as top-level, do NOT include redirection object
        // Include tournamentSeasonName only if seasonName exists (SEASON type, not DEFAULT)
        const { redirectionType, url, academyId, academyName, coachingCenterId, location, academyLocation, tournamentId, tournamentName, sports, tournamentSeason, seasonName, tabName, tabType, weeklyReportId, tabIndex, ...rest } = btn;
        const payload: any = {
          ...rest,
          redirectionType: 'TournamentTab',
          tournamentId,
          tournamentName,
          sports,
          tabName,
        };
        if (tabType) {
          payload.tabType = tabType;
        }
        if (tournamentSeason) {
          payload.tournamentSeasonId = tournamentSeason;
        }
        // Only include tournamentSeasonName if seasonName exists (not DEFAULT season)
        if (seasonName) {
          payload.tournamentSeasonName = seasonName;
        }
        return payload;
      } else if (btn.redirectionType === 'WeeklyReport') {
        // For WeeklyReport, send weeklyReportId as a top-level field
        const { redirectionType, url, page, academyId, academyName, coachingCenterId, location, academyLocation, tournamentId, tournamentName, sports, tabName, tabType, weeklyReportId, tabIndex, ...rest } = btn;
        return {
          ...rest,
          redirectionType: 'WeeklyReport',
          weeklyReportId,
        };
      } else if (btn.redirectionType === 'Page') {
        // For Page, send redirection object with type=page and path from page input
        redirection = {
          type: 'page',
          path: btn.page || '',
        };
      } else if (btn.redirectionType === 'Custom') {
        // For Custom, send user-entered type/path as-is
        redirection = {
          type: btn.customType || '',
          path: btn.customPath || '',
        };
      } else if (String(btn.redirectionType).toLowerCase() === 'url') {
        redirection = {
          // API expects lowercase casing for redirect type
          type: 'url',
          path: btn.url || '',
        };
      }
      const {
        redirectionType, url, page, customType, customPath, academyId, academyName, coachingCenterId, location, academyLocation, tournamentId, tournamentName, sports, tournamentSeason, seasonName, tabName, tabType, weeklyReportId, tabIndex, ...rest
      } = btn;
      // For all other types, include redirection object as before
      return {
        ...rest,
        redirection,
        ...(tabIndex !== undefined ? { tabIndex } : {}),
      };
    }
    if (btn.category === 'share') {
      return {
        ...btn,
        redirection: { type: 'share', path: '' },
      };
    }
    return btn;
  });
}

// Update getCompressionOptions to support field/platform-specific logic
function getCompressionOptions(file: File, field: 'main' | 'mobile', platform: string) {
  const sizeKB = file.size / 1024;
  const maxWidthOrHeight = 1600;
  if (platform === 'web' && field === 'main') {
    // Banner Image (Web): quality 90%, no size restriction
    return { initialQuality: 0.9, maxWidthOrHeight, useWebWorker: true };
  } else if (platform === 'web' && field === 'mobile') {
    // Mobile View Image (Web): quality 90%, maxSizeMB 0.25 (250 KB)
    return { maxSizeMB: 0.25, initialQuality: 0.9, maxWidthOrHeight, useWebWorker: true };
  } else if (sizeKB < 120) {
    // Small images: skip compression
    return null;
  } else {
    // Default: high quality, slightly larger max size
    return { maxSizeMB: 0.15, maxWidthOrHeight, initialQuality: 0.92, useWebWorker: true };
  }
}

export const BannerForm: React.FC<BannerFormProps> = ({
  show,
  onHide,
  banner,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<Banner>>(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [inAppState, setInAppState] = useState<{ type: string; fields: any }>({ type: '', fields: {} });
  const [academySearch, setAcademySearch] = useState('');
  const [academyOptions, setAcademyOptions] = useState<any[]>([]);
  const [academyLoading, setAcademyLoading] = useState(false);
  const academySearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [weeklyReportSearch, setWeeklyReportSearch] = useState('');
  const [weeklyReportOptions, setWeeklyReportOptions] = useState<any[]>([]);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const weeklyReportSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [weeklyReportDropdownOpen, setWeeklyReportDropdownOpen] = useState(false);
  const [tournamentSearch, setTournamentSearch] = useState('');
  const [tournamentOptions, setTournamentOptions] = useState<any[]>([]);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [tournamentDropdownOpen, setTournamentDropdownOpen] = useState(false);
  const tournamentSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [initialFormData, setInitialFormData] = useState<Partial<Banner> | null>(null);
  const [inAppLoading, setInAppLoading] = useState(false);
  const [mobileImagePreview, setMobileImagePreview] = useState<string | null>(null);
  const [isMobileUploading, setIsMobileUploading] = useState(false);
  const [mobileUploadProgress, setMobileUploadProgress] = useState(0);
  const [mobileUploadSuccess, setMobileUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [bannerImageError, setBannerImageError] = useState<string | null>(null);
  const [mobileImageError, setMobileImageError] = useState<string | null>(null);

  const fetchBannerDetails = async () => {
    if (!banner || !banner.id) return;
    setInAppLoading(true);
    try {
      setLoading(true);
      const bannerData = await getBannerDetails(banner.id);
      const mappedData: Partial<Banner> = {
        id: bannerData.id,
        title: bannerData.title,
        image: bannerData.image,
        bannerLocation: bannerData.bannerLocation as 'home' | 'tournament' | 'playerVault',
        type: bannerData.type as 'plain' | 'custom',
        textHtml: bannerData.textHtml,
        sortNo: bannerData.sortNo,
        hasMiscProperty: bannerData.hasMiscProperty,
        platform: bannerData.platform,
        extraButtons: bannerData.extraButtons || [],
      };
      setFormData(mappedData);
      console.log('FormData extraButtons:', mappedData.extraButtons);
      setInitialFormData(mappedData);
      if (bannerData.image) {
        setImagePreview(getImageUrl(bannerData.image));
      }
      if (bannerData.mobileViewImage) {
        setMobileImagePreview(getImageUrl(bannerData.mobileViewImage));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch banner details');
    } finally {
      setLoading(false);
      setInAppLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      if (banner && banner.id) {
        fetchBannerDetails();
      } else {
        const initialData = getInitialFormData();
        setFormData(initialData);
        setInitialFormData(initialData);
        setImagePreview(null);
        setError(null);
        setUploadProgress(0);
        setUploadSuccess(false);
        setIsUploading(false);
        setInAppState({ type: '', fields: {} });
        setInitialFormData(null);
      }
    }
  }, [show, banner]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
        if (name === 'bannerLocation' || name === 'type' || name === 'platform') {
            setFormData(prev => ({ ...prev, [name]: value as any }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
  };

  const validateImage = (file: File): string | null => {
    if (file.size > 5 * 1024 * 1024) { // 5MB
      return 'Image size should not exceed 5MB.';
    }
    return null;
  };

  const handleImageUpload = async (file: File) => {
    const validationError = validateImage(file);
    if (validationError) {
      setBannerImageError(validationError);
      return;
    }
    setBannerImageError(null);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadSuccess(false);
    setError(null);
    try {
      const options = getCompressionOptions(file, 'main', formData.platform || 'web');
      let processedFile = file;
      if (options) {
        processedFile = await imageCompression(file, { ...options, onProgress: setUploadProgress });
      }
      const imageUrl = await uploadBannerImage(processedFile, 'main');
      setFormData(prev => ({ ...prev, image: imageUrl }));
      setUploadSuccess(true);
      setImagePreview(URL.createObjectURL(processedFile));
    } catch (err) {
      setBannerImageError('Image upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMobileImageUpload = async (file: File) => {
    const validationError = validateImage(file);
    if (validationError) {
      setMobileImageError(validationError);
      return;
    }
    setMobileImageError(null);
    setIsMobileUploading(true);
    setMobileUploadProgress(0);
    setMobileUploadSuccess(false);
    setError(null);
    try {
      const options = getCompressionOptions(file, 'mobile', formData.platform || 'web');
      let processedFile = file;
      if (options) {
        processedFile = await imageCompression(file, { ...options, onProgress: setMobileUploadProgress });
      }
      const imageUrl = await uploadBannerImage(processedFile, 'mobile');
      setFormData(prev => ({ ...prev, mobileViewImage: imageUrl }));
      setMobileUploadSuccess(true);
      setMobileImagePreview(URL.createObjectURL(processedFile));
    } catch (err) {
      setMobileImageError('Mobile image upload failed. Please try again.');
    } finally {
      setIsMobileUploading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  }, []);

  const validateUrl = (url: string): boolean => {
    return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.image) {
      setError('Banner image is required.');
      return;
    }

    
    setLoading(true);

    const payload = { ...formData, extraButtons: transformExtraButtonsToApi(formData.extraButtons || []) };
    if (!banner?.id) {
      delete payload.id;
    }

    try {
      if (banner && banner.id) {
        await updateBanner({ id: banner.id, payload: payload as Partial<Banner> });
      } else {
        delete payload.id;
        await addBanner(payload as Omit<Banner, 'id'>);
      }
      
      onSuccess?.();
      onHide();
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleExtraButtonsChange = (buttons: ExtraButton[]) => {
    setFormData(prev => ({ ...prev, extraButtons: buttons }));
  };

  const isFormDirty = () => {
    if (!initialFormData) return true; // New form is always "dirty"
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  const isFormValid = () => {
    const isCustom = formData.type === 'custom';
    const hasExtraButtons = (formData.extraButtons || []).length > 0;
    const sortNoValid = typeof formData.sortNo === 'number' && formData.sortNo >= 1 && formData.sortNo <= 20;
    return (
      !!formData.image &&
      (!isCustom || hasExtraButtons) &&
      areExtraButtonsValid(formData.extraButtons || []) &&
      sortNoValid
    );
  };

  const completeBannerData: Banner = {
    id: formData.id || '',
    title: formData.title || '',
    image: formData.image || '',
    bannerLocation: formData.bannerLocation || 'home',
    type: formData.type || 'plain',
    textHtml: formData.textHtml || '',
    sortNo: formData.sortNo || 1,
    hasMiscProperty: formData.hasMiscProperty || false,
    platform: formData.platform || 'app',
    extraButtons: formData.extraButtons || [],
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{banner ? 'Edit Banner' : 'Add New Banner'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <ShimmerLoader type="form" />
        ) : (
          <>
            {error && !error.toLowerCase().includes('image') && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title || ''}
                      onChange={handleInputChange}
                      placeholder="Enter banner title (optional)"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="isActive-switch"
                      label="Active"
                      checked={formData.isActive !== false}
                      onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Platform</Form.Label>
                    <Select
                      name="platform"
                      value={{ value: formData.platform || 'app', label: (formData.platform === 'web' ? 'Web' : 'App') }}
                      options={[
                        { value: 'app', label: 'App' },
                        { value: 'web', label: 'Web' }
                      ]}
                      onChange={option => {
                        if (option) setFormData(prev => ({ ...prev, platform: option.value }));
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Banner Location</Form.Label>
                    <Select
                      name="bannerLocation"
                      value={(() => {
                        const loc = formData.bannerLocation || 'home';
                        const labelMap: Record<string, string> = { home: 'Home', tournament: 'Tournament', playerVault: 'Player Vault' };
                        return { value: loc, label: labelMap[loc] || 'Home' };
                      })()}
                      options={[
                        { value: 'home', label: 'Home' },
                        { value: 'tournament', label: 'Tournament' },
                        { value: 'playerVault', label: 'Player Vault' },
                      ]}
                      onChange={option => {
                        if (option) setFormData(prev => ({ ...prev, bannerLocation: option.value }));
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Banner Type</Form.Label>
                    <Select
                      name="type"
                      value={{ value: formData.type || 'plain', label: (formData.type === 'custom' ? 'Custom' : 'Plain') }}
                      options={[
                        { value: 'plain', label: 'Plain' },
                        { value: 'custom', label: 'Custom' }
                      ]}
                      onChange={option => {
                        if (option) setFormData(prev => ({ ...prev, type: option.value }));
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description (HTML)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="textHtml"
                  value={formData.textHtml || ''}
                  onChange={handleInputChange}
                  placeholder="Enter HTML for the description"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  name="sortNo"
                  value={formData.sortNo === undefined ? '' : formData.sortNo}
                  min={1}
                  max={20}
                  onChange={e => {
                    const value = e.target.value;
                    // Allow empty string for deletion
                    if (value === '') {
                      setFormData(prev => ({ ...prev, sortNo: undefined }));
                    } else {
                      let num = Number(value);
                      if (num > 20) num = 20;
                      if (num < 1) num = 1;
                      setFormData(prev => ({ ...prev, sortNo: num }));
                    }
                  }}
                  onWheel={e => e.currentTarget.blur()}
                />
              </Form.Group>

              {/* Banner and Mobile View Image Upload Side by Side */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Banner Image</Form.Label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={e => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleImageUpload(file);
                      }}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                    />
                    <div className="mt-3">
                      <div
                        style={{
                          position: 'relative',
                          width: 300,
                          height: 200,
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: '1px solid #dee2e6',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {imagePreview ? (
                          <>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 8,
                              }}
                            />
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                fileInputRef.current?.click();
                              }}
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(255,255,255,0.8)',
                                border: 'none',
                                borderRadius: '50%',
                                padding: 4,
                                cursor: 'pointer',
                                zIndex: 2,
                              }}
                              title="Edit Image"
                            >
                              <i className="bi bi-pencil" style={{ color: '#007bff', fontSize: 18 }} />
                            </button>
                            {imagePreview && (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setFormData(prev => ({ ...prev, image: '' }));
                                  setImagePreview(null);
                                  setBannerImageError(null);
                                }}
                                style={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  background: 'rgba(244,67,54,0.9)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  padding: 4,
                                  cursor: 'pointer',
                                  zIndex: 2,
                                }}
                                title="Remove Image"
                                disabled={loading || isUploading}
                              >
                                <i className="bi bi-trash" style={{ fontSize: 18 }} />
                              </button>
                            )}
                          </>
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 20,
                              textAlign: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-cloud-upload display-4"></i>
                            <p className="mt-2 mb-0">
                              Drag and drop an image here, or click to select
                            </p>
                            <small className="text-muted">
                              Max size: 5MB, Supported formats: JPEG, PNG
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                    {bannerImageError && (
                      <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 14, marginLeft: 4 }}>
                        {bannerImageError}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                {formData.platform === 'web' && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile View Image</Form.Label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={e => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) handleMobileImageUpload(file);
                        }}
                        id="mobile-image-upload"
                        style={{ display: 'none' }}
                      />
                      <div className="mt-3">
                        <div
                          style={{
                            position: 'relative',
                            width: 300,
                            height: 200,
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: '1px solid #dee2e6',
                            background: '#f8f9fa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          onClick={() => document.getElementById('mobile-image-upload')?.click()}
                        >
                          {mobileImagePreview ? (
                            <>
                              <img
                                src={mobileImagePreview}
                                alt="Mobile banner preview"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                }}
                              />
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  document.getElementById('mobile-image-upload')?.click();
                                }}
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  background: 'rgba(255,255,255,0.8)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  padding: 4,
                                  cursor: 'pointer',
                                  zIndex: 2,
                                }}
                                title="Edit Image"
                              >
                                <i className="bi bi-pencil" style={{ color: '#007bff', fontSize: 18 }} />
                              </button>
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setFormData(prev => ({ ...prev, mobileViewImage: '' }));
                                  setMobileImagePreview(null);
                                }}
                                style={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  background: 'rgba(244,67,54,0.9)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  padding: 4,
                                  cursor: 'pointer',
                                  zIndex: 2,
                                }}
                                title="Remove Image"
                                disabled={loading || isMobileUploading}
                              >
                                <i className="bi bi-trash" style={{ fontSize: 18 }} />
                              </button>
                            </>
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 20,
                                textAlign: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <i className="bi bi-cloud-upload display-4"></i>
                              <p className="mt-2 mb-0">
                                Drag and drop an image here, or click to select
                              </p>
                              <small className="text-muted">
                                Max size: 1MB, Supported formats: JPEG, PNG
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                      {mobileImageError && (
                        <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 14, marginLeft: 4 }}>
                          {mobileImageError}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Extra Buttons */}
              <ExtraButtonsForm
                buttons={formData.extraButtons || []}
                onChange={handleExtraButtonsChange}
                platform={formData.platform}
              />

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!isFormValid() || (banner && !isFormDirty()) || loading}
                >
                  {loading ? 'Saving...' : banner ? 'Update Banner' : 'Create Banner'}
                </Button>
                <Button variant="light" onClick={onHide} disabled={loading}>
                  Cancel
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
      {showPreview && (
        <BannerPreview
          show={showPreview}
          onHide={() => setShowPreview(false)}
          banner={completeBannerData}
          imageUrl={imagePreview || getImageUrl(completeBannerData.image)}
        />
      )}
    </Modal>
  );
}; 